import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/Modal';
import type { ClassModuleAssignment, ClassTimeWindow } from '@/types/admin';
import { Plus, Trash2 } from 'lucide-react';
import './ClassModuleAssignmentModal.css';

// System.DayOfWeek: 0 = Sonntag ... 6 = Samstag. Anzeige beginnt bei Montag.
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
  const mins = (minutes % 60).toString().padStart(2, '0');
  return `${hours}:${mins}`;
};

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const formatDateForInput = (date?: string | Date): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface ClassTimeWindowDraft {
  weekday: number;
  start: string;
  end: string;
}

interface EditingClassModule {
  classId: string;
  moduleId: string;
  className: string;
  moduleName: string;
}

export interface ClassModuleAssignmentModalProps {
  editing: EditingClassModule | null;
  initialAssignment?: ClassModuleAssignment;
  initialTimeWindows: ClassTimeWindow[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (request: {
    moduleId: string;
    startDate?: string;
    endDate?: string;
    timeWindows: ClassTimeWindow[];
  }) => Promise<void> | void;
}

export const ClassModuleAssignmentModal: React.FC<ClassModuleAssignmentModalProps> = ({
  editing,
  initialAssignment,
  initialTimeWindows,
  isSaving,
  onClose,
  onSave,
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeWindows, setTimeWindows] = useState<ClassTimeWindowDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      setStartDate(formatDateForInput(initialAssignment?.startDate));
      setEndDate(formatDateForInput(initialAssignment?.endDate));
      setTimeWindows(initialTimeWindows.map(window => ({
        weekday: window.weekday,
        start: minutesToTime(window.startMinutes),
        end: minutesToTime(window.endMinutes),
      })));
      setError(null);
    }
  }, [editing, initialAssignment, initialTimeWindows]);

  const addTimeWindowRow = () => {
    setTimeWindows(current => [...current, { weekday: 1, start: '08:00', end: '10:00' }]);
  };

  const removeTimeWindowRow = (index: number) => {
    setTimeWindows(current => current.filter((_, i) => i !== index));
  };

  const updateTimeWindowRow = (index: number, changes: Partial<ClassTimeWindowDraft>) => {
    setTimeWindows(current => current.map((window, i) => (i === index ? { ...window, ...changes } : window)));
  };

  const handleSave = async () => {
    if (timeWindows.some(window => timeToMinutes(window.start) >= timeToMinutes(window.end))) {
      setError('In every time window, the start time must be before the end time.');
      return;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setError('The start date must be before the end date.');
      return;
    }

    const convertedWindows: ClassTimeWindow[] = timeWindows.map(window => ({
      weekday: window.weekday,
      startMinutes: timeToMinutes(window.start),
      endMinutes: timeToMinutes(window.end),
    }));

    await onSave({
      moduleId: editing!.moduleId,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      timeWindows: convertedWindows,
    });
  };

  if (!editing) return null;

  return (
    <Modal
      footer={(
        <>
          <button className="btn btn-secondary" disabled={isSaving} onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" disabled={isSaving} onClick={() => void handleSave()} type="button">
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </>
      )}
      onClose={onClose}
      title={`Assignment ${editing.className} – ${editing.moduleName}`}
    >
      {error && <p className="modal-error">{error}</p>}

      <div className="modal-field">
        <label htmlFor="assignment-start-date">Start date</label>
        <input
          id="assignment-start-date"
          onChange={(event) => setStartDate(event.target.value)}
          type="date"
          value={startDate}
        />
        <p className="modal-field-hint">Leave empty if the module should be available immediately.</p>
      </div>

      <div className="modal-field">
        <label htmlFor="assignment-end-date">End date</label>
        <input
          id="assignment-end-date"
          onChange={(event) => setEndDate(event.target.value)}
          type="date"
          value={endDate}
        />
        <p className="modal-field-hint">Leave empty if the module should be available indefinitely.</p>
      </div>

      <div className="modal-field">
        <label>Conversation time windows</label>
        <p className="modal-field-hint">Without time windows, conversations are possible at any time. With time windows, only within the specified times.</p>
        {timeWindows.map((window, index) => (
          <div className="time-window-row" key={index}>
            <select aria-label="Weekday" onChange={(event) => updateTimeWindowRow(index, { weekday: Number(event.target.value) })} value={window.weekday}>
              {WEEKDAYS.map(weekday => (
                <option key={weekday.value} value={weekday.value}>{weekday.label}</option>
              ))}
            </select>
            <input aria-label="Start time" onChange={(event) => updateTimeWindowRow(index, { start: event.target.value })} type="time" value={window.start} />
            <span>–</span>
            <input aria-label="End time" onChange={(event) => updateTimeWindowRow(index, { end: event.target.value })} type="time" value={window.end} />
            <button aria-label="Remove time window" className="class-action class-action-danger" onClick={() => removeTimeWindowRow(index)} type="button"><Trash2 aria-hidden="true" size={16} /></button>
          </div>
        ))}
        <button className="btn btn-secondary" onClick={addTimeWindowRow} type="button"><Plus aria-hidden="true" size={16} /> Add time window</button>
      </div>
    </Modal>
  );
};
